import { Box, TextField, Typography } from '@mui/material'
import React, { useState } from 'react'
import { EmcNetworkScanButton } from '../../../components/Dialogs/components/NetworkScanButton'
import { Emc, ModbusTcpChargingStation } from 'src/Libs/flow/installation/forms'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { EvsePreviewImage } from './helpers/EvsePreviewImage'
import { ModbusTcpPairingEditor } from './helpers/ModbusTcpPairingEditor'
import WittySolarImage from 'src/Libs/flow/images/Witty_solar_XEV1KS22T2S.png'
import WittyLinkImage from 'src/Libs/flow/images/Witty_Link.png'
import WittyFlexImage from 'src/Libs/flow/images/Edsn_Blue.png'
import { wittyFlexReference } from 'src/Libs/wallboxes/models/wallbox-reference'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useDialogActions } from '../../../components/Dialogs/hooks'
import { getCleanMacAddressPairingId, formatModbusTcpPairingId, isModbusTcpPairingIdDuplicate, isValidModbusTcpPairingId } from '../..'

//#region Component

export interface ModbusTcpPairingDialogProps {
  installationId: number
  emc: Emc
  modbusTcp: ModbusTcpChargingStation
  modbusTcpChargingStations: ModbusTcpChargingStation[]
  submitResult: (result: { pairingId: string; reference: string; offline: boolean; ip: string; macOriginVerified: boolean }) => void
  onClose?: () => void
}

/** Dialog for Modbus TCP pairing on evses charging stations */
export const ModbusTcpPairingDialog: React.FC<ModbusTcpPairingDialogProps> = ({
  installationId,
  emc,
  modbusTcp,
  modbusTcpChargingStations,
  submitResult,
  onClose,
}) => {
  const { t } = useCustomTranslation()
  const [pairingId, setPairingId] = useState(modbusTcp.pairingId)
  const [deviceReference, setDeviceReference] = useState(modbusTcp.deviceReference)
  const [startNetworkScanner, setStartNetworkScanner] = useState(false)
  const [offline, setOffline] = useState(false)
  const [ip, setIp] = useState('')
  const [macOriginVerified, setMacOriginVerified] = useState(false)

  const initialPairingId = modbusTcp.pairingId
  const isInvalidModbusTcpEvsePairingId = !isValidModbusTcpPairingId(pairingId)
  const usedCleanPairingIds = modbusTcpChargingStations.map(({ pairingId }) => getCleanMacAddressPairingId(pairingId))
  const isDuplicatePairingId = isModbusTcpPairingIdDuplicate(pairingId, initialPairingId, usedCleanPairingIds)

  const isEmpty = !pairingId.trim()

  const getError = (): string | undefined => {
    if (!isEmpty && isInvalidModbusTcpEvsePairingId) {
      return t('installation.productEditor.pairingEditor.invalid-modbus-tcp-pairing-id')
    }
    if (isDuplicatePairingId) {
      return t('installation.productEditor.pairingEditor.duplicate-modbus-tcp-pairing-id')
    }
    return undefined
  }

  const error = getError()

  const isSame = initialPairingId === pairingId

  const handleSubmit = (): void => {
    /** Dashes and semicolons are allow during typing, but must be removed on submition. */
    const validPairingId = formatModbusTcpPairingId(pairingId)
    if (!validPairingId) return

    submitResult({
      pairingId: validPairingId,
      reference: deviceReference,
      offline,
      ip,
      macOriginVerified,
    })

    onClose?.()
  }

  const {
    featureFlags: { useEvseWittyLinkSubTypes },
  } = useFeatureFlagsContext()

  const handleOnClose = (): void => {
    onClose?.()
  }

  const primaryActionName = modbusTcp.isNew
    ? t('installation.productEditor.pairingEditor.save')
    : t('installation.productEditor.pairingEditor.pairAndSave')
  const isPrimaryActionDisabled = !!error || isSame || isEmpty
  const dialogActions = useDialogActions({
    primary: {
      name: primaryActionName,
      onClick: handleSubmit,
      disabled: isPrimaryActionDisabled,
    },
    secondary: { onClick: handleOnClose },
  })

  return (
    <Dialog title={t('installation.productEditor.pairingEditor.title.evse')} maxWidth={'md'} onClose={handleOnClose} actions={dialogActions}>
      <Box
        sx={{
          display: 'grid',
          my: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'auto minmax(0, 1fr)' },
        }}
      >
        {modbusTcp.reference === 'XEV1K07T2S / XEV1K22T2S' && <EvsePreviewImage reference={WittySolarImage} width={120} height={228} />}
        {modbusTcp.reference === 'XVL122C / XVL122S' && <EvsePreviewImage reference={WittyLinkImage} width={120} height={168} />}
        {modbusTcp.reference === wittyFlexReference && <EvsePreviewImage reference={WittyFlexImage} width={120} height={228} />}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {useEvseWittyLinkSubTypes && (
            <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
              <TextField label={t('installation.productEditor.brand')} value={modbusTcp.brand} disabled fullWidth />
              <TextField label={t('installation.productEditor.reference')} value={deviceReference || modbusTcp.reference} disabled fullWidth />
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: { sx: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              name="Emc pairing id"
              label={t('productEditor.pairingEditor.pairingID.modbus-wallbox')}
              fullWidth
              value={pairingId}
              onChange={e => setPairingId(e.target.value.trim())}
              inputProps={{ maxLength: 50 }}
              error={!!error}
              helperText={error}
            />

            <EmcNetworkScanButton emc={emc} installationId={installationId} onClick={() => setStartNetworkScanner(!startNetworkScanner)} />
          </Box>
          <Typography variant="body2">{t('installation.productEditor.pairingEditor.wallboxRefWarningMessage')}</Typography>
        </Box>
      </Box>

      <Box>
        {startNetworkScanner && (
          <ModbusTcpPairingEditor
            installationId={installationId}
            emcId={emc?.deviceId.toString()}
            evseDeviceId={modbusTcp.deviceId}
            currentPairingId={pairingId}
            setPairingResult={({ pairingId, deviceReference, ip }) => {
              setPairingId(pairingId)
              setDeviceReference(deviceReference)
              setOffline(true)
              setIp(ip)
              setMacOriginVerified(true)
            }}
            usedCleanPairingIds={usedCleanPairingIds}
          />
        )}
      </Box>
    </Dialog>
  )
}
